/**
 * @fileoverview CrmFailuresPanel — recent CRM sync failures list for the
 * SuperAdmin → Operations → CRM subtab.
 *
 * Renders reverse-chronological failed sync rows (from `GET /admin/crm/failures`)
 * as a table with per-row Retry and "File as feedback" actions. The parent owns
 * the actual sync/feedback work; this component is presentational and only
 * dispatches the two callbacks. While a specific row is retrying, its Retry
 * button shows a spinner and is disabled so a double-click is a no-op.
 *
 * Table columns:
 *   Entity | Name | Error | Last failure | Count | Actions
 *
 * The Error cell truncates its value at 80 characters (visible), with the full
 * error string exposed via a `title` attribute so an operator can hover to see
 * the complete text without the row expanding.
 */

import React from 'react';
import PropTypes from 'prop-types';

const ERROR_TRUNCATE_LEN = 80;
const MS_PER_MIN = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Truncate a string to the visible-column max length, appending an ellipsis
 * when clipping happens. Handles null/undefined/non-string inputs gracefully.
 *
 * @param {?string} value - The raw error snippet from the backend.
 * @param {number} [max=ERROR_TRUNCATE_LEN] - Maximum visible length.
 * @returns {string} Clipped string (with ellipsis) or empty string when input is falsy.
 */
function truncate(value, max = ERROR_TRUNCATE_LEN) {
  if (!value) return '';
  const s = String(value);
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

/**
 * Format an ISO timestamp as a compact relative time string.
 * Falls back to a localized date string for anything older than a week.
 *
 * @param {?string} iso - ISO 8601 timestamp, or null/undefined.
 * @returns {string} e.g. "just now", "3m ago", "2h ago", "yesterday", "3d ago", or a date.
 */
function formatRelativeTime(iso) {
  if (!iso) return 'never';
  const then = new Date(iso);
  const t = then.getTime();
  if (Number.isNaN(t)) return 'never';
  const diff = Date.now() - t;
  if (diff < MS_PER_MIN) return 'just now';
  if (diff < MS_PER_HOUR) return `${Math.floor(diff / MS_PER_MIN)}m ago`;
  if (diff < MS_PER_DAY) return `${Math.floor(diff / MS_PER_HOUR)}h ago`;
  const days = Math.floor(diff / MS_PER_DAY);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString();
}

/**
 * Build the Set-lookup key for a failure row. Kept as a helper so the
 * component and any tests share the exact same encoding.
 *
 * @param {string} entityType - e.g. 'vendor'
 * @param {number|string} entityId - The cogslog row's numeric id.
 * @returns {string} `${entity_type}:${entity_id}`
 */
function retryKey(entityType, entityId) {
  return `${entityType}:${entityId}`;
}

/**
 * Recent CRM sync failures panel with per-row Retry + File as feedback actions.
 *
 * @param {Object} props
 * @param {Array<{entity_type:string, entity_id:number, display_name:string,
 *                last_error:string, last_error_at:string, error_count:number}>} props.failures
 *   Reverse-chronological failed sync rows. Empty array renders the empty state.
 * @param {(entityType:string, entityId:number) => void} props.onRetry
 *   Invoked when the operator clicks the Retry button on a row.
 * @param {(failureRow:Object) => void} props.onFileAsFeedback
 *   Invoked with the full failure row when the operator clicks "File as feedback".
 * @param {Set<string>} [props.retrying]
 *   Set of `${entity_type}:${entity_id}` keys currently mid-retry. Matching rows
 *   show a spinner on Retry and disable the button.
 * @returns {JSX.Element}
 */
function CrmFailuresPanel({
  failures,
  onRetry,
  onFileAsFeedback,
  retrying = undefined,
}) {
  const rows = Array.isArray(failures) ? failures : [];
  const inFlight = retrying instanceof Set ? retrying : new Set();

  if (rows.length === 0) {
    return (
      <div
        className="empty-state"
        role="status"
        data-testid="crm-failures-panel"
      >
        <h3 className="empty-state__title">No recent sync failures. 🎉</h3>
        <p className="empty-state__description">
          Every recorded CRM sync landed cleanly.
        </p>
      </div>
    );
  }

  return (
    <div
      className="tbl-wrap crm-failures-panel"
      data-testid="crm-failures-panel"
    >
      <table>
        <thead>
          <tr>
            <th scope="col">Entity</th>
            <th scope="col">Name</th>
            <th scope="col">Error</th>
            <th scope="col">Last failure</th>
            <th scope="col" className="crm-failures-panel__num-header">Count</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = retryKey(row.entity_type, row.entity_id);
            const isRetrying = inFlight.has(key);
            const errorText = row.last_error || '';
            return (
              <tr
                key={key}
                data-testid={`crm-failure-row-${row.entity_type}-${row.entity_id}`}
              >
                <td className="crm-failures-panel__entity">
                  {row.entity_type}
                </td>
                <td className="crm-failures-panel__name">
                  <span className="crm-failures-panel__display-name">
                    {row.display_name || '—'}
                  </span>
                  {' '}
                  <span className="crm-failures-panel__id">
                    #{row.entity_id}
                  </span>
                </td>
                <td
                  className="crm-failures-panel__error"
                  title={errorText || undefined}
                >
                  {truncate(errorText)}
                </td>
                <td
                  className="crm-failures-panel__last"
                  title={row.last_error_at || undefined}
                >
                  {formatRelativeTime(row.last_error_at)}
                </td>
                <td className="crm-failures-panel__num">
                  {Number(row.error_count || 0).toLocaleString()}
                </td>
                <td className="crm-failures-panel__actions">
                  <button
                    type="button"
                    className={`btn btn-sm btn-secondary${isRetrying ? ' btn-loading' : ''}`}
                    onClick={() => {
                      if (isRetrying) return;
                      if (typeof onRetry === 'function') {
                        onRetry(row.entity_type, row.entity_id);
                      }
                    }}
                    disabled={isRetrying}
                    aria-label={`Retry CRM sync for ${row.entity_type} ${row.display_name || row.entity_id}`}
                    data-testid={`crm-failure-retry-${row.entity_type}-${row.entity_id}`}
                  >
                    {isRetrying ? 'Retrying…' : 'Retry'}
                  </button>
                  {' '}
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => {
                      if (typeof onFileAsFeedback === 'function') {
                        onFileAsFeedback(row);
                      }
                    }}
                    aria-label={`File feedback about CRM sync failure for ${row.entity_type} ${row.display_name || row.entity_id}`}
                    data-testid={`crm-failure-feedback-${row.entity_type}-${row.entity_id}`}
                  >
                    File as feedback
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

CrmFailuresPanel.propTypes = {
  /** Reverse-chronological failed sync rows. */
  failures: PropTypes.arrayOf(
    PropTypes.shape({
      entity_type: PropTypes.string.isRequired,
      entity_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      display_name: PropTypes.string,
      last_error: PropTypes.string,
      last_error_at: PropTypes.string,
      error_count: PropTypes.number,
    })
  ).isRequired,
  /** Fired when the operator clicks Retry on a row. */
  onRetry: PropTypes.func.isRequired,
  /** Fired with the full row when the operator clicks "File as feedback". */
  onFileAsFeedback: PropTypes.func.isRequired,
  /** Set of `${entity_type}:${entity_id}` keys currently mid-retry. */
  retrying: PropTypes.instanceOf(Set),
};

export default CrmFailuresPanel;
